import { RefObject, useEffect, useRef, useState } from 'react';
import type { ObjectDetection } from '@tensorflow-models/coco-ssd';
import { useExam } from '../context/ExamContext';
import { IncidentType, Severity } from '../types/domain';
import { makeIncident } from '../utils/incidents';

interface VisionState {
  faceVisible: boolean;
  faces: number;
  lookingAway: boolean;
  lowLight: boolean;
  objects: string[];
}

type AiStatus = 'disabled' | 'loading' | 'ready' | 'failed';

const ANALYSIS_INTERVAL_MS = 1800;
const initialVision: VisionState = {
  faceVisible: true,
  faces: 1,
  lookingAway: false,
  lowLight: false,
  objects: []
};

export function useAiProctor(videoRef: RefObject<HTMLVideoElement>, enabled: boolean, startedAt?: string) {
  const { addIncident, updateMetrics } = useExam();
  const [vision, setVision] = useState<VisionState>(initialVision);
  const [status, setStatus] = useState<AiStatus>(enabled ? 'loading' : 'disabled');
  const [error, setError] = useState<string>();
  const modelRef = useRef<ObjectDetection>();
  const activeDetections = useRef(new Set<IncidentType>());

  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
      setError(undefined);
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setError(undefined);

    void Promise.all([import('@tensorflow/tfjs'), import('@tensorflow-models/coco-ssd')])
      .then(async ([tf, cocoSsd]) => {
        await tf.ready();
        const model = await cocoSsd.load({ base: 'lite_mobilenet_v2' });
        if (cancelled) {
          model.dispose();
          return;
        }
        modelRef.current = model;
        setStatus('ready');
      })
      .catch((modelError: unknown) => {
        if (!cancelled) {
          setError(modelError instanceof Error ? modelError.message : 'Unable to load the AI model');
          setStatus('failed');
        }
      });

    return () => {
      cancelled = true;
      modelRef.current?.dispose();
      modelRef.current = undefined;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || status !== 'ready') return;
    let cancelled = false;
    let timer: number | undefined;

    const schedule = () => {
      if (!cancelled) timer = window.setTimeout(() => void analyze(), ANALYSIS_INTERVAL_MS);
    };

    const analyze = async () => {
      const video = videoRef.current;
      const model = modelRef.current;
      if (!video || video.readyState < 2 || !model) {
        schedule();
        return;
      }

      try {
        const light = estimateLight(video);
        const predictions = await model.detect(video, 10, 0.4);
        if (cancelled) return;

        const people = predictions.filter((item) => item.class === 'person' && item.score > 0.52);
        const phones = predictions.filter((item) => item.class === 'cell phone' && item.score > 0.45);
        const books = predictions.filter((item) => item.class === 'book' && item.score > 0.42);
        const laptops = predictions.filter((item) => item.class === 'laptop' && item.score > 0.45);
        const faceVisible = people.length > 0;
        const faces = people.length;
        const lookingAway = people[0]
          ? people[0].bbox[0] < video.videoWidth * 0.12 || people[0].bbox[0] > video.videoWidth * 0.55
          : false;
        const tooClose = people[0] ? people[0].bbox[2] > video.videoWidth * 0.76 : false;
        const tooFar = people[0] ? people[0].bbox[2] < video.videoWidth * 0.18 : false;

        setVision({
          faceVisible,
          faces,
          lookingAway,
          lowLight: light < 42,
          objects: predictions.filter((item) => item.score > 0.45).map((item) => item.class)
        });

        const detections: Array<[IncidentType, boolean, number, Severity]> = [
          ['face_missing', !faceVisible, 78, 'high'],
          ['multiple_faces', faces > 1, 82, 'critical'],
          ['looking_away', lookingAway, 64, 'medium'],
          ['phone_detected', phones.length > 0, Math.round((phones[0]?.score ?? 0.5) * 100), 'critical'],
          ['book_detected', books.length > 0, Math.round((books[0]?.score ?? 0.5) * 100), 'high'],
          ['laptop_detected', laptops.length > 0, Math.round((laptops[0]?.score ?? 0.5) * 100), 'medium'],
          ['too_close', tooClose, 70, 'low'],
          ['too_far', tooFar, 70, 'low'],
          [light < 12 ? 'camera_covered' : 'low_light', light < 42, 76, 'medium']
        ];
        const nextActive = new Set(detections.filter(([, active]) => active).map(([type]) => type));
        const now = Date.now();
        const elapsed = startedAt ? Math.round((now - new Date(startedAt).getTime()) / 1000) : 0;
        for (const [type, active, confidence, severity] of detections) {
          if (active && !activeDetections.current.has(type)) {
            addIncident(makeIncident(type, confidence, severity, elapsed, 'camera', captureFrame(video)));
          }
        }
        activeDetections.current = nextActive;

        const riskScore = Math.min(
          100,
          Math.round(
            phones.length * 22 +
              Math.max(0, faces - 1) * 28 +
              (faceVisible ? 0 : 25) +
              (lookingAway ? 8 : 0) +
              (light < 42 ? 8 : 0)
          )
        );
        updateMetrics({
          riskScore,
          attentionScore: Math.max(0, 100 - riskScore - (lookingAway ? 12 : 0)),
          eyeContactPercent: lookingAway ? 62 : 93,
          faceVisibilityPercent: faceVisible ? 96 : 20,
          cameraHealth: light < 42 || !faceVisible ? 'warning' : 'good'
        });
      } catch (analysisError) {
        if (!cancelled) {
          setError(analysisError instanceof Error ? analysisError.message : 'AI frame analysis failed');
          setStatus('failed');
        }
        return;
      }
      schedule();
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [addIncident, enabled, startedAt, status, updateMetrics, videoRef]);

  return { vision, status, error };
}

function captureFrame(video: HTMLVideoElement): string | undefined {
  if (!video.videoWidth) return undefined;
  const canvas = document.createElement('canvas');
  canvas.width = 240;
  canvas.height = Math.round((video.videoHeight / video.videoWidth) * 240);
  const context = canvas.getContext('2d');
  context?.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.55);
}

function estimateLight(video: HTMLVideoElement): number {
  const canvas = document.createElement('canvas');
  canvas.width = 48;
  canvas.height = 27;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return 100;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  let total = 0;
  for (let index = 0; index < pixels.length; index += 4) {
    total += (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 3;
  }
  return total / (pixels.length / 4);
}
